// Pension arrangementen: de gedeelde vorm voor Producten › Pension arrangementen en de wizard Nieuwe reservering.
import { formatEuro } from "../../i18n/format";

export type ArrangementNiveau = "DIER" | "RESERVERING";
export type ArrangementEenheid = "PER_KEER" | "PER_DAG" | "PER_WEEK" | "PER_VERBLIJF";

export const NIVEAUS: ArrangementNiveau[] = ["DIER", "RESERVERING"];
export const EENHEDEN: ArrangementEenheid[] = ["PER_KEER", "PER_DAG", "PER_WEEK", "PER_VERBLIJF"];

/** Uit GET /api/pension/arrangementen. */
export interface Arrangement {
  id: number;
  vestigingId: number;
  naam: string;
  omschrijving: string | null;
  /** Een foto als data-URL. */
  foto: string | null;
  /** Leeg: voor elke diersoort. */
  diersoortId: number | null;
  diersoort: string | null;
  niveau: ArrangementNiveau;
  eenheid: ArrangementEenheid;
  /** De prijs is een vanafprijs. */
  vanaf: boolean;
  maxAantal: number | null;
  actief: boolean;
  positie: number;
  prijs: { bedrag: number; geldigVanaf: string | null; btwCodeId: number | null } | null;
}

type Vertaal = (key: string, options?: { args?: unknown[] }) => string;

/** "€ 23,50 per keer", "vanaf € 12,65 per week", of "Nog geen prijs". */
export function prijsTekst(a: Pick<Arrangement, "prijs" | "vanaf" | "eenheid">, t: Vertaal, locale: string): string {
  if (!a.prijs) return t("pensionArrangement.geenPrijs");
  const bedrag = formatEuro(a.prijs.bedrag, locale);
  return t(a.vanaf ? "pensionArrangement.prijs.vanaf" : "pensionArrangement.prijs.vast", {
    args: [bedrag, t(`pensionArrangement.eenheid.kort.${a.eenheid}`)],
  });
}

/**
 * Hoeveel keer de prijs telt: per keer het aantal, per dag ook maal de dagen, per week maal de begonnen weken, en
 * per verblijf één keer, hoe vaak ook gekozen.
 */
export function eenheden(eenheid: ArrangementEenheid, aantal: number, dagen: number): number {
  switch (eenheid) {
    case "PER_DAG":
      return aantal * Math.max(1, dagen);
    case "PER_WEEK":
      return aantal * Math.max(1, Math.ceil(dagen / 7));
    case "PER_VERBLIJF":
      return 1;
    default:
      return aantal;
  }
}

/**
 * Leest een foto in als data-URL en verkleint hem tot hooguit {@code max} pixels aan de lange kant, als JPEG. Zonder
 * canvas (in tests) blijft hij zoals hij is.
 */
export async function leesFoto(bestand: File, max = 900): Promise<string> {
  const origineel = await new Promise<string>((ok, fout) => {
    const lezer = new FileReader();
    lezer.onload = () => ok(String(lezer.result));
    lezer.onerror = () => fout(lezer.error);
    lezer.readAsDataURL(bestand);
  });
  if (typeof createImageBitmap !== "function") return origineel;
  try {
    const beeld = await createImageBitmap(bestand);
    const schaal = Math.min(1, max / Math.max(beeld.width, beeld.height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(beeld.width * schaal);
    canvas.height = Math.round(beeld.height * schaal);
    const tekenen = canvas.getContext("2d");
    if (!tekenen) return origineel;
    tekenen.drawImage(beeld, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL("image/jpeg", 0.82);
  } catch {
    return origineel;
  }
}
